import {
  AlertCircle,
  CheckCircle,
  Clock,
  ExternalLink,
  Eye,
  EyeOff,
  Gift,
  Loader2,
  Lock,
  Package,
  Printer,
  Save,
  User,
  X,
  XCircle,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { cancelOrder } from '../services/apiService';
import type { DbOrder } from '../types';

interface UserProfile {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  isGuest: boolean;
  totalOrders: number;
  spinCredit: number;
  spinsAvailable: number;
  memberSince: string;
}

interface SpinRecord {
  id: number;
  prize_label: string;
  prize_type: string;
  prize_value: number;
  spun_at: string;
}

type Tab = 'profile' | 'orders' | 'security';

const UserPanel: React.FC = () => {
  const { user, setShowAuthModal } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('profile');
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [orders, setOrders] = useState<DbOrder[]>([]);
  const [spins, setSpins] = useState<SpinRecord[]>([]);

  // Profile edit state
  const [editName, setEditName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Cancel order state
  const [cancellingOrderId, setCancellingOrderId] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    if (!user) {
      setShowAuthModal(true);
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;

    setLoading(true);
    try {
      // Fetch profile and orders in parallel
      const [profileRes, ordersRes] = await Promise.all([
        fetch('/api/user/profile', { headers: { Authorization: `Bearer ${token}` } }),
        fetch('/api/user/orders', { headers: { Authorization: `Bearer ${token}` } }),
      ]);

      if (profileRes.ok) {
        const data = await profileRes.json();
        setProfile(data.user);
        setEditName(data.user.name);
        setEditPhone(data.user.phone || '');
      }

      if (ordersRes.ok) {
        const data = await ordersRes.json();
        setOrders(data.orders || []);
        setSpins(data.spins || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    const token = localStorage.getItem('sessionToken');
    if (!token) return;

    setSaving(true);
    setSaveMessage(null);

    try {
      const response = await fetch('/api/user/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: editName, phone: editPhone }),
      });

      const data = await response.json();

      if (response.ok) {
        setSaveMessage({ type: 'success', text: 'Profile updated successfully!' });
        fetchData();
      } else {
        setSaveMessage({ type: 'error', text: data.error || 'Failed to update profile' });
      }
    } catch {
      setSaveMessage({ type: 'error', text: 'Failed to update profile' });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }

    if (newPassword.length < 6) {
      setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters' });
      return;
    }

    const token = localStorage.getItem('sessionToken');
    if (!token) return;

    setSaving(true);
    setPasswordMessage(null);

    try {
      const response = await fetch('/api/user/password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });

      const data = await response.json();

      if (response.ok) {
        setPasswordMessage({ type: 'success', text: 'Password changed successfully!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordMessage({ type: 'error', text: data.error || 'Failed to change password' });
      }
    } catch {
      setPasswordMessage({ type: 'error', text: 'Failed to change password' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancellingOrderId) return;

    setCancelLoading(true);
    try {
      const result = await cancelOrder(cancellingOrderId, cancelReason);

      // Update the order in state
      if (result.cancelled) {
        setOrders(
          orders.map((o) => (o.id === cancellingOrderId ? { ...o, status: 'cancelled' } : o))
        );
      } else if (result.cancellationRequested) {
        setOrders(
          orders.map((o) =>
            o.id === cancellingOrderId
              ? { ...o, cancellation_requested: 1, cancellation_reason: cancelReason }
              : o
          )
        );
      }

      alert(result.message);
      setShowCancelModal(false);
      setCancellingOrderId(null);
      setCancelReason('');
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to cancel order');
    } finally {
      setCancelLoading(false);
    }
  };

  const openCancelModal = (orderId: number) => {
    setCancellingOrderId(orderId);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'printing':
        return <Printer className="w-4 h-4 text-info" />;
      case 'ready':
        return <Package className="w-4 h-4 text-accent-purple" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-error" />;
      default:
        return <Clock className="w-4 h-4 text-warning" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'badge-completed';
      case 'printing':
        return 'badge-printing';
      case 'ready':
        return 'badge-ready';
      case 'cancelled':
        return 'badge-cancelled';
      default:
        return 'badge-pending';
    }
  };

  if (!user) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-info animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-black mb-2" style={{ color: 'var(--text-primary)' }}>
          My Account
        </h1>
        <p style={{ color: 'var(--text-secondary)' }}>Manage your profile, orders, and settings</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('profile')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
            activeTab === 'profile'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
              : 'glass nav-link-inactive'
          }`}
        >
          <User className="w-4 h-4" />
          Profile
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
            activeTab === 'orders'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
              : 'glass nav-link-inactive'
          }`}
        >
          <Package className="w-4 h-4" />
          Orders
          {orders.length > 0 && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs">{orders.length}</span>
          )}
        </button>
        {!profile?.isGuest && (
          <button
            onClick={() => setActiveTab('security')}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-bold transition-all ${
              activeTab === 'security'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-on-gradient'
                : 'glass nav-link-inactive'
            }`}
          >
            <Lock className="w-4 h-4" />
            Security
          </button>
        )}
      </div>

      {/* Profile Tab */}
      {activeTab === 'profile' && profile && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Profile Info */}
          <div
            className="glass rounded-2xl p-6 border"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <h2
              className="text-lg font-bold mb-4 flex items-center gap-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <User className="w-5 h-5 text-info" />
              Profile Information
            </h2>

            {saveMessage && (
              <div
                className={`mb-4 p-3 rounded-xl text-sm ${
                  saveMessage.type === 'success'
                    ? 'bg-green-500/20 border border-green-500/50 text-success'
                    : 'bg-red-500/20 border border-red-500/50 text-error'
                }`}
              >
                {saveMessage.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="theme-input w-full rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Email
                </label>
                <input
                  type="email"
                  value={profile.email || ''}
                  disabled
                  className="w-full bg-slate-50 dark:bg-slate-800/30 border border-slate-300 dark:border-slate-700 rounded-xl px-4 py-3 cursor-not-allowed"
                  style={{ color: 'var(--text-muted)' }}
                />
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  Email cannot be changed
                </p>
              </div>

              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Phone
                </label>
                <input
                  type="tel"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="(555) 123-4567"
                  className="theme-input w-full rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                />
              </div>

              <button
                onClick={handleSaveProfile}
                disabled={saving}
                className="btn-neon w-full disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                ) : (
                  <Save className="w-5 h-5 inline mr-2" />
                )}
                Save Changes
              </button>
            </div>
          </div>

          {/* Stats Card */}
          <div className="space-y-4">
            <div
              className="glass rounded-2xl p-6 border"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Account Stats
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: 'var(--surface-inactive)' }}
                >
                  <Package className="w-6 h-6 text-info mx-auto mb-2" />
                  <p className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>
                    {profile.totalOrders}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Total Orders
                  </p>
                </div>
                <div
                  className="rounded-xl p-4 text-center"
                  style={{ backgroundColor: 'var(--surface-inactive)' }}
                >
                  <Gift className="w-6 h-6 text-warning mx-auto mb-2" />
                  <p className="text-2xl font-black text-warning">{profile.spinsAvailable}</p>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                    Spins Available
                  </p>
                </div>
              </div>
            </div>

            {profile.isGuest && (
              <div className="glass rounded-2xl p-6 border border-yellow-500/30">
                <AlertCircle className="w-6 h-6 text-warning mb-2" />
                <h3 className="font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                  Guest Account
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Create a full account to save your order history and earn rewards!
                </p>
                <button className="btn-neon text-sm">Upgrade Account</button>
              </div>
            )}

            <div
              className="glass rounded-2xl p-6 border"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Member since{' '}
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {new Date(profile.memberSince).toLocaleDateString()}
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div
              className="glass rounded-2xl p-8 border text-center"
              style={{ borderColor: 'var(--border-color)' }}
            >
              <Package className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
              <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                No orders yet
              </h3>
              <p className="mb-4" style={{ color: 'var(--text-secondary)' }}>
                Start by ordering your first 3D print!
              </p>
              <button onClick={() => navigate('/')} className="btn-neon">
                Order Now
              </button>
            </div>
          ) : (
            orders.map((order) => (
              <div
                key={order.id}
                className={`glass rounded-2xl p-4 border ${order.status === 'cancelled' ? 'border-red-500/30 opacity-60' : ''}`}
                style={
                  order.status !== 'cancelled' ? { borderColor: 'var(--border-color)' } : undefined
                }
              >
                <div className="flex items-start gap-4">
                  {order.model_image_url && (
                    <img
                      src={order.model_image_url}
                      alt={order.model_name}
                      className="w-16 h-16 rounded-xl object-cover"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-bold truncate" style={{ color: 'var(--text-primary)' }}>
                          {order.model_name}
                        </h3>
                        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                          Order #{order.id} • {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${getStatusColor(
                          order.status
                        )}`}
                      >
                        {getStatusIcon(order.status)}
                        {order.status.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span style={{ color: 'var(--text-muted)' }}>
                        {order.print_time_hours}h • {order.filament_grams}g
                      </span>
                      <span className="text-success font-bold">${order.price.toFixed(2)}</span>
                      {order.model_url && (
                        <a
                          href={order.model_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-info hover:opacity-80 flex items-center gap-1"
                        >
                          <ExternalLink className="w-3 h-3" />
                          View Model
                        </a>
                      )}
                    </div>
                    {/* Cancellation status or button */}
                    {order.status !== 'cancelled' && order.status !== 'completed' && (
                      <div
                        className="mt-3 pt-3 border-t"
                        style={{ borderColor: 'var(--border-color)' }}
                      >
                        {order.cancellation_requested ? (
                          <div className="flex items-center gap-2 text-sm text-warning">
                            <Clock className="w-4 h-4" />
                            Cancellation requested - waiting for admin review
                          </div>
                        ) : order.cancellation_denied ? (
                          <div className="flex items-center gap-2 text-sm text-error">
                            <XCircle className="w-4 h-4" />
                            Admin denied your cancellation request
                          </div>
                        ) : (
                          <button
                            onClick={() => openCancelModal(order.id)}
                            className="flex items-center gap-1 text-sm text-error hover:opacity-80 transition-colors"
                          >
                            <XCircle className="w-4 h-4" />
                            {order.status === 'pending' ? 'Cancel Order' : 'Request Cancellation'}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}

          {/* Spin History */}
          {spins.length > 0 && (
            <div className="mt-8">
              <h2
                className="text-lg font-bold mb-4 flex items-center gap-2"
                style={{ color: 'var(--text-primary)' }}
              >
                <Gift className="w-5 h-5 text-warning" />
                Spin History
              </h2>
              <div
                className="glass rounded-2xl border overflow-hidden"
                style={{ borderColor: 'var(--border-color)' }}
              >
                <table className="w-full text-sm">
                  <thead className="table-header">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Prize</th>
                      <th className="px-4 py-3 text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-themed">
                    {spins.map((spin) => (
                      <tr key={spin.id}>
                        <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>
                          {new Date(spin.spun_at).toLocaleDateString()}
                        </td>
                        <td
                          className="px-4 py-3 font-medium"
                          style={{ color: 'var(--text-primary)' }}
                        >
                          {spin.prize_label}
                        </td>
                        <td className="px-4 py-3 text-right text-success">
                          {spin.prize_type === 'nothing'
                            ? '-'
                            : spin.prize_type === 'discount_percent'
                              ? `${spin.prize_value}% off`
                              : spin.prize_type === 'discount_fixed'
                                ? `$${spin.prize_value} off`
                                : 'Free item'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Security Tab */}
      {activeTab === 'security' && !profile?.isGuest && (
        <div className="max-w-md">
          <div
            className="glass rounded-2xl p-6 border"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <h2
              className="text-lg font-bold mb-4 flex items-center gap-2"
              style={{ color: 'var(--text-primary)' }}
            >
              <Lock className="w-5 h-5 text-info" />
              Change Password
            </h2>

            {passwordMessage && (
              <div
                className={`mb-4 p-3 rounded-xl text-sm ${
                  passwordMessage.type === 'success'
                    ? 'bg-green-500/20 border border-green-500/50 text-success'
                    : 'bg-red-500/20 border border-red-500/50 text-error'
                }`}
              >
                {passwordMessage.text}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Current Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="theme-input w-full rounded-xl px-4 py-3 pr-10 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  New Password
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="theme-input w-full rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                />
              </div>

              <div>
                <label
                  className="block text-sm font-bold mb-2"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  Confirm New Password
                </label>
                <input
                  type={showPasswords ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="theme-input w-full rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none"
                />
              </div>

              <button
                type="button"
                onClick={() => setShowPasswords(!showPasswords)}
                className="text-sm flex items-center gap-1 hover:opacity-80"
                style={{ color: 'var(--text-muted)' }}
              >
                {showPasswords ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPasswords ? 'Hide passwords' : 'Show passwords'}
              </button>

              <button
                onClick={handleChangePassword}
                disabled={saving || !currentPassword || !newPassword || !confirmPassword}
                className="btn-neon w-full disabled:opacity-50"
              >
                {saving ? (
                  <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                ) : (
                  <Lock className="w-5 h-5 inline mr-2" />
                )}
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="glass rounded-2xl p-6 w-full max-w-md border"
            style={{ borderColor: 'var(--border-color)' }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                {orders.find((o) => o.id === cancellingOrderId)?.status === 'pending'
                  ? 'Cancel Order'
                  : 'Request Cancellation'}
              </h3>
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingOrderId(null);
                  setCancelReason('');
                }}
                className="hover:opacity-80"
                style={{ color: 'var(--text-muted)' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              {orders.find((o) => o.id === cancellingOrderId)?.status === 'pending'
                ? 'This order will be cancelled immediately.'
                : 'Your cancellation request will be sent to the admin for review.'}
            </p>

            <div className="mb-4">
              <label
                className="block text-sm font-bold mb-2"
                style={{ color: 'var(--text-secondary)' }}
              >
                Reason (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Why do you want to cancel?"
                className="theme-input w-full rounded-xl px-4 py-3 focus:ring-2 focus:ring-red-500 focus:border-transparent focus:outline-none resize-none h-24"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancellingOrderId(null);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 border rounded-xl hover:opacity-80 transition-colors"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }}
              >
                Keep Order
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={cancelLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {cancelLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <XCircle className="w-4 h-4" />
                )}
                {orders.find((o) => o.id === cancellingOrderId)?.status === 'pending'
                  ? 'Cancel Order'
                  : 'Request Cancellation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserPanel;
